import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import { useFetchData } from 'services/api/useFetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { baseUrl } from 'App';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import CompanyDetailsEditForm from './CompanyDetailsEditForm';
import '../../../style/scss/card.scss';

interface CompanyDetailsCardProps {
    division?: { id: number, is_main: boolean, name?: string };
}

// Shows the company details of a division as stacked lines with the logo and an edit button
const CompanyDetailsCard: React.FC<CompanyDetailsCardProps> = ({ division: divisionProp }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { environmentHash } = useAuthContext();
    const { selectedDivision, isEnvironmentSelected, refetchDivisions } = useSettingsDivision();

    // Use the given division or fallback to the division selected in the settings menu
    const division = divisionProp || (!isEnvironmentSelected && selectedDivision ? selectedDivision : undefined);

    // Determine whether the card shows the main division
    const isMain = !division || division.is_main;

    // Fetch the company details of the main division or the given division
    const apiUrl = isMain
        ? `get_main_company_details/${environmentHash}`
        : `get_division_company_details/${division!.id}`;
    const { response: companyDetails, loading, refetch } = useFetchData({ apiUrl });

    // Configure the logo path based on the server environment
    const logoPath = companyDetails?.company_logo
        ? (process.env.NODE_ENV === 'production' ? companyDetails.company_logo : `${baseUrl}${companyDetails.company_logo}`)
        : null;

    // Open the edit modal for the company details
    const handleEdit = () => {
        initializeModal(
            <CompanyDetailsEditForm division={division} />, {
                title: t('company_details.general.edit_company_details_title'),
                modalSize: 'medium',
                // Refetch the divisions so the division selector shows a changed division name
                onSuccess: () => {
                    refetch();
                    refetchDivisions();
                },
            }
        )
    };

    // Compose the stacked value lines of the card
    const valueLines: (string | null)[] = companyDetails ? [
        companyDetails.company_name,
        [companyDetails.street, companyDetails.house_number].filter(Boolean).join(' ') || null,
        [companyDetails.postal_code, companyDetails.city].filter(Boolean).join(' ') || null,
        companyDetails.country,
        companyDetails.email,
        companyDetails.phone_number,
        companyDetails.website,
        companyDetails.coc_number,
        companyDetails.vat_number,
        companyDetails.iban_number,
    ] : [];

    // Determine whether only the company name is filled in
    const hasOnlyCompanyName = valueLines.filter(Boolean).length <= 1;

    return (
        <div className='card details-card'
             onClick={() => handleEdit()}>
            <div className='card-edit-header'>
                <h6>{t('nav.company_details_label')}</h6>
                <span className='edit-icon'>
                    <FontAwesomeIcon
                        icon={faPen}
                        onClick={() => handleEdit()}
                    />
                </span>
            </div>
            {loading === 'show-loader' ? (
                <div className="loader"></div>
            ) : companyDetails && (
                <div className='details-card-content'>
                    <div className='details-card-values'>
                        {valueLines.map((line, index) => (
                            line ? <p key={index}>{line}</p> : null
                        ))}
                        {hasOnlyCompanyName && (
                            <p className='empty-details-placeholder'>
                                {t('company_details.general.empty_details_placeholder')}
                            </p>
                        )}
                    </div>
                    {logoPath && (
                        <div className='details-card-logo'>
                            <img src={logoPath} alt={companyDetails.company_name} />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
};

export default CompanyDetailsCard;
